import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api';
import { CHAT_QUOTA_QUERY_KEY, extractChatQuota } from '@/hooks/queries/useAi';
import { AI_CREDITS_STATUS_QUERY_KEY, extractCreditsStatus } from '@/hooks/queries/useAiCredits';
import { normalizeAiMessage } from '@/lib/aiMessage';
import type {
  AiConversation,
  AiFeedback,
  AiMessage,
  AiToolCall,
  ChatSendResult,
  CreateConversationResult,
} from '@/types/ai';

interface CreateConversationApiPayload {
  conversation: AiConversation;
  message?: AiMessage;
}

function normalizeAssistantMessage(payload: unknown): AiMessage {
  if (
    payload !== null &&
    typeof payload === 'object' &&
    'message' in payload &&
    payload.message !== null &&
    typeof payload.message === 'object'
  ) {
    return normalizeAiMessage(payload.message as AiMessage);
  }

  return normalizeAiMessage(payload as AiMessage);
}

function updateQuotaFromAnyHeaders(
  queryClient: ReturnType<typeof useQueryClient>,
  headers?: Headers | Record<string, string> | null,
) {
  const quota = extractChatQuota(headers);
  if (quota) {
    queryClient.setQueryData(CHAT_QUOTA_QUERY_KEY, quota);
  }

  if (headers instanceof Headers) {
    const creditsStatus = extractCreditsStatus(headers);
    if (creditsStatus) {
      queryClient.setQueryData(AI_CREDITS_STATUS_QUERY_KEY, creditsStatus);
    }
  }
}

export const PRODUCT_MUTATING_TOOLS = new Set(['create_product', 'update_stock', 'update_price']);

function hasToolCall(toolCalls: AiToolCall[] | null | undefined, toolSet: Set<string>): boolean {
  return toolCalls?.some((tc) => toolSet.has(tc.tool)) ?? false;
}

function updateQuotaFromError(queryClient: ReturnType<typeof useQueryClient>, error: unknown) {
  if (error instanceof ApiError) {
    updateQuotaFromAnyHeaders(queryClient, error.headers);
    // Après une erreur 402 (crédits insuffisants), forcer un refresh du solde
    if (error.status === 402) {
      void queryClient.invalidateQueries({ queryKey: AI_CREDITS_STATUS_QUERY_KEY });
    }
  }
}

export function useMarkInsightRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (insightId: string) => api.post(`/ai/insights/${insightId}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai', 'insights'] }),
  });
}

export function useDismissInsight() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (insightId: string) => api.post(`/ai/insights/${insightId}/dismiss`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai', 'insights'] }),
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: 0,
    mutationFn: async (message: string): Promise<CreateConversationResult> => {
      const response = await api.postWithMeta<CreateConversationApiPayload>('/ai/chat', {
        message,
      });
      const quota = extractChatQuota(response.headers);

      return {
        conversation: response.body.data.conversation,
        assistant: response.body.data.message
          ? normalizeAiMessage(response.body.data.message)
          : null,
        quota,
      };
    },
    onSuccess: (result) => {
      if (result.quota) {
        queryClient.setQueryData(CHAT_QUOTA_QUERY_KEY, result.quota);
      }
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
      void queryClient.invalidateQueries({ queryKey: AI_CREDITS_STATUS_QUERY_KEY });
      if (result.assistant && hasToolCall(result.assistant.tool_calls, PRODUCT_MUTATING_TOOLS)) {
        void queryClient.invalidateQueries({ queryKey: ['products'] });
      }
    },
    onError: (error) => updateQuotaFromError(queryClient, error),
  });
}

export interface ChatSendInput {
  message: string;
  image?: File;
}

export function useSendChatMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    retry: 0,
    mutationFn: async (input: string | ChatSendInput): Promise<ChatSendResult> => {
      const { message, image } = typeof input === 'string' ? { message: input } : input;

      // Vision : une image jointe part en multipart (pas de streaming ni de retry —
      // le backend encode l'image en base64 pour Anthropic Vision).
      if (image) {
        const formData = new FormData();
        formData.append('message', message);
        formData.append('image', image);
        const body = await api.upload<AiMessage | { message: AiMessage }>(
          `/ai/chat/${conversationId}/messages`,
          formData,
        );
        return {
          assistant: normalizeAssistantMessage(body.data),
          quota: null,
        };
      }

      const MAX_RETRIES = 2;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 30_000);

        try {
          const response = await api.postWithMeta<AiMessage | { message: AiMessage }>(
            `/ai/chat/${conversationId}/messages`,
            { message },
            { signal: controller.signal },
          );

          return {
            assistant: normalizeAssistantMessage(response.body.data),
            quota: extractChatQuota(response.headers),
          };
        } catch (error) {
          window.clearTimeout(timeout);

          // Retry with exponential backoff on Anthropic rate limit
          if (
            error instanceof ApiError &&
            error.code === 'AI_RATE_LIMITED' &&
            attempt < MAX_RETRIES
          ) {
            await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
            continue;
          }

          if (error instanceof DOMException && error.name === 'AbortError') {
            throw new ApiError(
              'La requête a pris trop de temps. Veuillez réessayer.',
              'AI_TIMEOUT',
              408,
            );
          }
          throw error;
        } finally {
          window.clearTimeout(timeout);
        }
      }

      // Unreachable — loop always returns or throws — but satisfies TypeScript
      throw new ApiError(
        "L'assistant est temporairement surchargé. Veuillez patienter quelques secondes puis réessayer.",
        'AI_RATE_LIMITED',
        429,
      );
    },
    onSuccess: (result) => {
      if (result.quota) {
        queryClient.setQueryData(CHAT_QUOTA_QUERY_KEY, result.quota);
      }
      // NOTE: Do NOT invalidate ['ai', 'conversation', conversationId] here.
      // ChatThread manages local messages and triggers the refetch itself
      // after cleaning up local copies to avoid duplicate rendering.
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
      void queryClient.invalidateQueries({ queryKey: AI_CREDITS_STATUS_QUERY_KEY });
      if (hasToolCall(result.assistant.tool_calls, PRODUCT_MUTATING_TOOLS)) {
        void queryClient.invalidateQueries({ queryKey: ['products'] });
      }
    },
    onError: (error) => updateQuotaFromError(queryClient, error),
  });
}

export function useMessageFeedback(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      messageId,
      feedback,
    }: {
      messageId: string;
      feedback: NonNullable<AiFeedback>;
    }) => {
      await api.post(`/ai/chat/${conversationId}/messages/${messageId}/feedback`, { feedback });
      return { messageId, feedback };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversation', conversationId] });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/ai/chat/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] }),
  });
}

export function useRenameConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      await api.patch(`/ai/chat/${id}`, { title });
      return { id, title };
    },
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversation', id] });
    },
  });
}
